import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { availabilityApi, usersApi } from '@/api';
import { useInspectorProfile } from '@/contexts/InspectorProfileContext';
import type { AvailabilityConfig, User } from '@/types';
import { Skeleton } from '@/ui';
import { Stepper } from './Stepper';
import { STEPS, type StepId } from './types';
import { CompanyInfoStep } from './steps/CompanyInfoStep';
import { LogoStep } from './steps/LogoStep';
import { AgreementStep } from './steps/AgreementStep';
import { BookingStep } from './steps/BookingStep';
import { PaymentStep } from './steps/PaymentStep';
import { SpectoraStep } from './steps/SpectoraStep';
import { DoneStep } from './steps/DoneStep';

const EMPTY_AVAILABILITY: AvailabilityConfig = { days: [], blockedDates: [] };

// Determine which steps the inspector has already completed enough of that
// we can mark them done in the stepper. "Completed" here means data was
// persisted, not that the user clicked Next — important for resume cases
// where someone closes the tab mid-flow.
function deriveCompletedSteps(
  profile: User,
  availability: AvailabilityConfig,
): Set<StepId> {
  const done = new Set<StepId>();
  // firstName/lastName excluded on purpose: Clerk auto-fills them from
  // Google SSO, so their presence isn't evidence the inspector engaged with
  // the company step. companyName/phone/licenseNumber are user-typed.
  if (profile.companyName || profile.phone || profile.licenseNumber) {
    done.add('company');
  }
  if (profile.logoUrl) done.add('logo');
  if (profile.agreementTemplate && profile.agreementTemplate.trim().length > 0) {
    done.add('agreement');
  }
  if (profile.slug && availability.days.some((d) => d.isActive)) {
    done.add('booking');
  }
  if (profile.paymentMethodUrl || profile.paymentInstructions) {
    done.add('payment');
  }
  // 'spectora' doesn't have a completion signal stored on the profile —
  // its outcome is a template that lives separately. Starts "not done" and
  // only flips once the user advances past it in this session.
  return done;
}

// First incomplete step — where to start the wizard on resume. Falls back to
// 'done' when everything is filled (not expected to happen in practice
// because the gate would have already short-circuited).
function pickResumeStep(completed: Set<StepId>): StepId {
  for (const step of STEPS) {
    if (!completed.has(step.id)) return step.id;
  }
  return 'done';
}

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const { setProfile: setSharedProfile } = useInspectorProfile();

  const [profile, setProfile] = useState<User | null>(null);
  const [availability, setAvailability] = useState<AvailabilityConfig>(EMPTY_AVAILABILITY);
  const [currentStep, setCurrentStep] = useState<StepId>('company');
  // Track per-session what happened on each step we advanced past:
  //   sessionSaved   — user persisted data for this step in this session
  //   sessionSkipped — user advanced past without saving
  // The two are mutually exclusive; visiting the same step twice with a
  // different outcome moves it between sets via setSessionSaved/Skipped below.
  const [sessionSaved, setSessionSaved] = useState<Set<StepId>>(new Set());
  const [sessionSkipped, setSessionSkipped] = useState<Set<StepId>>(new Set());
  const [loading, setLoading] = useState(true);

  // Initial load — fetch profile + availability in parallel and pick the
  // resume step. If the user already completed onboarding (e.g. they hit
  // /onboarding directly), bounce them to /inspections.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [profileResp, availabilityResp] = await Promise.all([
          usersApi.getProfile(),
          availabilityApi.get(),
        ]);
        if (cancelled) return;

        if (!profileResp.success || !profileResp.data) {
          navigate('/inspections', { replace: true });
          return;
        }
        const loadedProfile = profileResp.data;
        const loadedAvailability =
          availabilityResp.success && availabilityResp.data ? availabilityResp.data : EMPTY_AVAILABILITY;

        if (loadedProfile.onboardingCompleted) {
          navigate('/inspections', { replace: true });
          return;
        }

        setProfile(loadedProfile);
        setAvailability(loadedAvailability);
        setCurrentStep(pickResumeStep(deriveCompletedSteps(loadedProfile, loadedAvailability)));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const completedSteps = useMemo(() => {
    if (!profile) return new Set<StepId>();
    const fromData = deriveCompletedSteps(profile, availability);
    sessionSaved.forEach((id) => fromData.add(id));
    return fromData;
  }, [profile, availability, sessionSaved]);

  // Skipped only counts if the step doesn't ALSO have persisted data — a step
  // can't be both done-with-data and skipped, and "done" wins visually.
  const skippedSteps = useMemo(() => {
    const result = new Set<StepId>();
    sessionSkipped.forEach((id) => {
      if (!completedSteps.has(id)) result.add(id);
    });
    return result;
  }, [sessionSkipped, completedSteps]);

  const advance = (kind: 'saved' | 'skipped', nextStep?: StepId) => {
    if (kind === 'saved') {
      setSessionSaved((prev) => new Set(prev).add(currentStep));
      setSessionSkipped((prev) => {
        if (!prev.has(currentStep)) return prev;
        const next = new Set(prev);
        next.delete(currentStep);
        return next;
      });
    } else {
      setSessionSkipped((prev) => new Set(prev).add(currentStep));
    }
    if (nextStep) {
      setCurrentStep(nextStep);
      return;
    }
    const idx = STEPS.findIndex((s) => s.id === currentStep);
    const next = STEPS[idx + 1];
    if (next) setCurrentStep(next.id);
  };

  const handleProfileSaved = (updated: User) => {
    setProfile(updated);
    setSharedProfile(updated);
    advance('saved');
  };

  const handleBookingSaved = (updated: User, updatedAvailability: AvailabilityConfig) => {
    setProfile(updated);
    setSharedProfile(updated);
    setAvailability(updatedAvailability);
    advance('saved');
  };

  const handleFinished = (updated: User) => {
    setProfile(updated);
    setSharedProfile(updated);
    navigate('/inspections', { replace: true });
  };

  const handleStepClick = (id: StepId) => setCurrentStep(id);

  if (loading || !profile) {
    return (
      <div className="min-h-screen bg-surface-1 flex items-center justify-center p-6">
        <div className="w-full max-w-3xl space-y-4">
          <Skeleton className="h-8 w-1/3" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const stepBody = (() => {
    switch (currentStep) {
      case 'company':
        return (
          <CompanyInfoStep
            profile={profile}
            onSaved={handleProfileSaved}
            onSkip={() => advance('skipped')}
          />
        );
      case 'logo':
        return (
          <LogoStep
            profile={profile}
            onSaved={handleProfileSaved}
            onSkip={() => advance('skipped')}
          />
        );
      case 'agreement':
        return (
          <AgreementStep
            profile={profile}
            onSaved={handleProfileSaved}
            onSkip={() => advance('skipped')}
          />
        );
      case 'booking':
        return (
          <BookingStep
            profile={profile}
            availability={availability}
            onSaved={handleBookingSaved}
            onSkip={() => advance('skipped')}
          />
        );
      case 'payment':
        return (
          <PaymentStep
            profile={profile}
            onSaved={handleProfileSaved}
            onSkip={() => advance('skipped')}
          />
        );
      case 'spectora':
        // onDone fires after a successful XLSX import — track that as
        // "saved" so the stepper marks it green. Skip leaves it dimmed.
        return (
          <SpectoraStep
            onDone={() => advance('saved')}
            onSkip={() => advance('skipped')}
          />
        );
      case 'done':
        return <DoneStep profile={profile} onFinished={handleFinished} />;
    }
  })();

  return (
    // pt-[env(safe-area-inset-top)] pushes the header below the iOS status
    // bar / notch. Without it the "WELCOME TO FLOWSPECT" eyebrow sits
    // directly under the time + battery indicators on iPhone.
    // overflow-x-hidden guards against any single overflowing child (e.g.
    // long booking-slug origin) propagating horizontal scroll to the page.
    <div className="min-h-screen bg-surface-1 overflow-x-hidden pt-[env(safe-area-inset-top)]">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
        <header className="mb-6 lg:mb-10">
          <p className="text-caption text-ink-subtle uppercase tracking-wide">Welcome to Flowspect</p>
          <h1 className="text-heading-xl text-ink">Let's set up your account</h1>
        </header>
        <div className="grid gap-6 lg:gap-10 lg:grid-cols-[260px_1fr]">
          <Stepper
            steps={STEPS}
            currentStep={currentStep}
            completedSteps={completedSteps}
            skippedSteps={skippedSteps}
            onStepClick={handleStepClick}
          />
          <main className="min-w-0 rounded-2xl border border-border bg-surface-0 p-6 sm:p-8 shadow-sm">
            {stepBody}
          </main>
        </div>
      </div>
    </div>
  );
}
